import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Placeholder,
  Row,
} from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { FormGroup, Input, Label } from "reactstrap";
import { z } from "zod";
import Loader from "../../../../components/Common/Loader";
import {
  GET_ALL_CONFIGS,
  GET_HOTELS_LIST_FOR_FULL_PANEL_HANDLE_API,
  ROOMESSENTAILLIST,
  ROOMTYPE,
  ROOMTYPEADD,
  ROOMTYPEDETAIL,
  ROOMTYPEUPDATE
} from "../../../../services/ApiCalls";
import multipleFileUploader from "../../../../utilities/multipleFileUploader";
import { catchAsync, checkResponse } from "../../../../utilities/utilities";
import PricingRow from "./PricingRow";

const pricingItemSchema = z
  .object({
    pricingType: z.enum(["hour", "day"], {
      required_error: "Pricing type is required",
    }),
    priceCategory: z.coerce.string().nonempty("Required"),
    startDate: z.coerce.string().optional(),
    endDate: z.coerce.string().optional(),


    durationHours: z.coerce.number().optional(),
    minStay: z.coerce.number().optional(),

    basePrice: z.coerce
      .number()
      .min(1, "Base price is required"),

    discount: z.coerce
      .number()
      .min(0)
      .max(100)
      .default(0),

    finalPrice: z.coerce.number(),
  })
  .superRefine((data, ctx) => {
    if (data.pricingType === "hour" && !data.durationHours) {
      ctx.addIssue({
        path: ["durationHours"],
        message: "Duration hours is required for hourly pricing",
        code: z.ZodIssueCode.custom,
      });
    }
     if (data.priceCategory === "special" && !data.startDate) {
      ctx.addIssue({
        path: ["startDate"],
        message: "start Date is required for special category",
        code: z.ZodIssueCode.custom,
      });
    }

    if (data.priceCategory === "special" && !data.endDate) {
      ctx.addIssue({
        path: ["endDate"],
        message: "End Date is required for special category",
        code: z.ZodIssueCode.custom,
      });
    }
  });


const roomSchema = z.object({
  hotelId: z.string().nonempty("Hotel is required"),
  roomType: z.string().nonempty("Room type is required"),
  totalRoom: z.coerce
    .number()
    .int("Total room must be an integer")
    .min(1, "Total room must be greater than 0"),

  maxOccupancy: z.coerce
    .number()
    .int("Max people must be an integer")
    .min(1, "Number of people must be greater than 0"),

  availableRoom: z.coerce
    .number()
    .int("Available room must be an integer")
    .min(1, "Available Room is required"),

  // extraChildPrice: z.coerce
  //   .number()
  //   .int("Extra Child Price must be an integer")
  //   .min(1, "Extra Child Price is required"),
  // maxChilOccupancy: z.coerce
  //   .number()
  //   .int("Max Child Occupancy must be an integer")
  //   .min(1, "Max Child Occupancy is required"),

  extraAdultPrice: z
    .number()
    .int("Additional Adult Price must be an integer")
    .min(1, "Additional Adult Price is required"),
    checkInDate: z.string().nonempty("Date Required"),
    checkOutDate: z.string().nonempty("Date Required"),


  description: z.string().optional(),
  images: z
    .array(z.string())
    .min(1, "At least one image is required"),
  amenities: z.array(z.string()).min(1, "Select at least one amenity"),
  status: z.enum(["Active", "Inactive", "Deleted"], {
    required_error: "Status is required",
  }),
  pricingData: z
    .array(pricingItemSchema)
    .min(1, "At least one pricing type is required")
    .superRefine((items, ctx) => {
      const hourMap = {};
      // const dayMap = {};
      // const nightMap = {};

      items.forEach((item, index) => {
        // ⏱ Hour → durationHours must be unique
        if (item.pricingType === "hour" && item.durationHours) {
          if (hourMap[item.durationHours]) {
            ctx.addIssue({
              path: [index, "durationHours"],
              message: "This duration already exists for hourly pricing",
              code: z.ZodIssueCode.custom,
            });
          } else {
            hourMap[item.durationHours] = true;
          }
        }

        // // 🌞 Day → minStay must be unique
        // if (item.pricingType === "day" && item.minStay) {
        //   if (dayMap[item.minStay]) {
        //     ctx.addIssue({
        //       path: [index, "minStay"],
        //       message: "This min stay already exists for day pricing",
        //       code: z.ZodIssueCode.custom,
        //     });
        //   } else {
        //     dayMap[item.minStay] = true;
        //   }
        // }
      });
    }),
});

const AddRoom = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const navigate = useNavigate();
  const [imagePreviews, setImagePreviews] = useState([]);
  const [imgloader, setImgLoader] = useState(false);
  const [roomAmenities, setRoomAmenities] = useState([]);
  const [hotelAdminData, setHotelData] = useState([]);
  const [roomTypeData, setRoomTypeData] = useState([]);
  const [loader, setLoader] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(roomSchema),
    defaultValues: {
      hotelId: "",
      amenities: [],
      totalRoom: "",
      extraAdultPrice: "",
      images: [],
      roomType: "",
      status: "Available",
      maxOccupancy: "",
      availableRoom: "",
      checkInDate: "",
        checkOutDate: "",
      baseOccupancy: "",
      extraChildPrice: "0",
      maxChilOccupancy: "0",
      description: "",
      pricingData: [
        {
          pricingType: "hour",
          priceCategory:"",
          durationHours: "",
          startDate:"",
          endDate:"",
          minStay: 1,
          basePrice: "",
          discount: 0,
          finalPrice: 0,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "pricingData",
  });
  const pricingData = watch("pricingData");



  const getAmenityData = catchAsync(async () => {
    const [roomRes, hotelRes, roomTypeRes] = await Promise.all([
      ROOMESSENTAILLIST({ limit: 100, page: 1, status: "Active" }),
      GET_HOTELS_LIST_FOR_FULL_PANEL_HANDLE_API(),
      ROOMTYPE({ limit: 100, page: 1, status: "Active" }),
    ]);


    checkResponse({ res: roomRes, setData: setRoomAmenities });
    checkResponse({ res: hotelRes, setData: setHotelData });
    if (!id) {
      setValue("hotelId", localStorage.getItem("hotelId"))
    }
    checkResponse({ res: roomTypeRes, setData: setRoomTypeData });
    if(id)
    getData()
  });

  const getData = catchAsync(async () => {
    setLoader(true);
    const res = await ROOMTYPEDETAIL(id);
    const success = checkResponse({
      res,
      setLoader,
    });
    if (success && res?.data?.data) {
      const data = res.data.data;
      setImagePreviews(data.images || []);
      reset({
        hotelName: data?.hotelId?.hotelName || "",
        hotelId: data.hotelId?._id || "",
        amenities: data.amenities?.map((a) => a._id) || [],
        totalRoom: data.totalRoom || "",
        extraAdultPrice: data.extraAdultPrice || "",
        extraChildPrice: data.extraChildPrice || "",
        maxChilOccupancy: data.maxChilOccupancy || "",
         checkInDate: data.checkInDate || "",
        checkOutDate: data.checkOutDate || "",
        roomType: data.roomType?._id || "",
        status: data.status || "",
        maxOccupancy: data?.maxOccupancy || "",
        availableRoom: data?.availableRoom || "",
        description: data?.description || "",
        images: data?.images?.map((ele) => ele?._id) || [],
        pricingData: data.pricingData.map((item) => ({
          _id: item._id,
          pricingType: item.pricingType,
          priceCategory:item.priceCategory,
          durationHours: item.durationHours,
          startDate: new Date(item.startDate).toISOString().split("T")[0],
          endDate: new Date(item.endDate).toISOString().split("T")[0],
          minStay:  1,
          basePrice: item.basePrice,
          discount: item.discount,
          finalPrice: item.finalPrice,
        })),
      });
    }
  }, setLoader);

  useEffect(() => {
    getAmenityData();
    if (id) {
      getData();
    }
  }, [id]);


  useEffect(() => {
    pricingData?.forEach((item, index) => {
      const base = Number(item.basePrice) || 0;
      const discount = Number(item.discount) || 0;

      const final = base - (base * discount) / 100;

      setValue(
        `pricingData.${index}.finalPrice`,
        final > 0 ? Math.round(final) : 0
      );
    });
  }, [pricingData, setValue]);

  const handleCheckbox = (field, value, checked) => {
    const currentValues = watch("amenities") || [];
    let updated;
    if (checked) {
      updated = [...currentValues, value];
    } else {
      updated = currentValues.filter((id) => id !== value);
    }
    setValue("amenities", updated, { shouldValidate: true });
  };

  const handleImageChange = catchAsync(async (e) => {
    setImgLoader(true);

    const files = Array.from(e.target.files);
    for (let file of files) {
      const res = await multipleFileUploader(file);

      if (Array.isArray(res)) {
        const currentIds = watch("images") || [];
        const newIds = res.map((img) => img._id);
        setValue("images", [...currentIds, ...newIds], {
          shouldValidate: true,
        });
        setImagePreviews((prev) => [...prev, ...res]);
      }
    }

    setImgLoader(false);
  });

  const handleRemoveImage = (index) => {
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
    const currentIds = watch("images") || [];
    const updatedIds = currentIds.filter((_, i) => i !== index);

    setValue("images", updatedIds, { shouldValidate: true });
  };

const [ptypes,setptypes]=useState([])
const [pctypes,setpctypes]=useState([])
    useEffect(()=>{
        GET_ALL_CONFIGS().then((res)=>{
            setptypes(res.data.data.pricingType)
            setpctypes(res.data.data.priceCategory)
        })
    },[])
  const submitHandler = catchAsync(async (data) => {
    const body = {
      ...data,
      images: data.images?.map((ele) => ele?._id ?? ele), // already array of IDs
      ...(id && { _id: id }),
    };
    console.log("body", body);


    const res = await (id ? ROOMTYPEUPDATE(body) : ROOMTYPEADD(body));
    const success = checkResponse({ res, showSuccess: true });
    if (success) navigate(`/roomtype?page=${page}`);
  });

  const handleCancel = () => navigate(`/roomtype?page=${page}`);


  return (
    <>

      {loader && <Loader />}
      <section className="addRoom position-relative py-3">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-10">
                <Link
                  to={`/roomtype?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    Width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {id ? t("roomType.editRoomType") : t("roomType.addRoomType")}
                </h4>
              </div>
            </Col>

            <Col lg="12" className="my-2">
              <div>
                <Form onSubmit={handleSubmit(submitHandler)}>
                  <Row className="justify-content-between">
                    <Col lg="6" className="my-2">
                      <label>{t("roomType.selectHotel")}</label>
                      <select className="form-control" {...register("hotelId")}>
                        <option value="">-- Select Hotel --</option>
                        {hotelAdminData?.hotelList?.map((admin) => (
                          <option key={admin?.id} value={admin?.id}>
                            {admin?.name}
                          </option>
                        ))}
                      </select>
                      {errors?.hotelId && (
                        <p className="text-danger">{errors.hotelId.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label> {t("roomType.roomType")} </label>
                      <select className="form-control" {...register("roomType")}>
                        <option value="">-- Select Room Type --</option>
                        {roomTypeData?.map((admin) => (
                          <option key={admin._id} value={admin._id}>
                            {admin?.name}
                          </option>
                        ))}
                      </select>
                      {errors?.roomType && (
                        <p className="text-danger">{errors.roomType.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("roomType.columns.totalRoom")}</label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("totalRoom", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.totalRoom && (
                        <p className="text-danger">
                          {errors.totalRoom.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>
                        {t("roomType.columns.availRoom")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("availableRoom", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.availableRoom && (
                        <p className="text-danger">{errors.availableRoom.message}</p>
                      )}
                    </Col>


                    {/* <Col lg="6" className="my-2">
                      <label>
                        {t("roomType.columns.extraChildPrice", "Extra Child Price")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("extraChildPrice", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.extraChildPrice && (
                        <p className="text-danger">{errors.extraChildPrice.message}</p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>
                        {t("roomType.columns.maxChilOccupancy", "Max Child Occupancy")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("maxChilOccupancy", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.maxChilOccupancy && (
                        <p className="text-danger">{errors.maxChilOccupancy.message}</p>
                      )}
                    </Col> */}
                    <Col lg="6" className="my-2">
                      <label>
                        {t("roomType.columns.maxPeopleOccupancy","Max People Occupancy")}
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        min={0}
                        {...register("maxOccupancy", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.maxOccupancy && (
                        <p className="text-danger">
                          {errors.maxOccupancy.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("roomType.columns.addAdultPrice")}</label>
                      <input
                        type="number"
                        className="form-control"
                        {...register("extraAdultPrice", {
                          valueAsNumber: true,
                          min: 0,
                        })}
                        onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.extraAdultPrice && (
                        <p className="text-danger">
                          {errors.extraAdultPrice.message}
                        </p>
                      )}
                    </Col>

                      <Col lg="6" className="my-2">
                      <label>{t("roomType.columns.checkInDate","Check In Date")}</label>
                      <input
                        type="date"
                        className="form-control"
                        {...register("checkInDate"
                        //   , {
                        //   valueAsNumber: true,
                        //   min: 0,
                        // }
                      )}
                        // onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.checkInDate && (
                        <p className="text-danger">
                          {errors.checkInDate.message}
                        </p>
                      )}
                    </Col>


                     <Col lg="6" className="my-2">
                      <label>{t("roomType.columns.checkOutDate","Check Out Date")}</label>
                      <input
                        type="date"
                        className="form-control"
                        {...register("checkOutDate"
                        //   , {
                        //   valueAsNumber: true,
                        //   min: 0,
                        // }
                      )}
                        // onKeyDown={(e) => e.key === "-" && e.preventDefault()}
                      />
                      {errors.checkOutDate && (
                        <p className="text-danger">
                          {errors.checkOutDate.message}
                        </p>
                      )}
                    </Col>

                    <Col lg="6" className="my-2">
                      <label>{t("roomType.columns.status")}</label>

                      <Form.Select {...register("status")}>
                        <option value="">Select Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Deleted">Deleted</option>
                      </Form.Select>
                    </Col>

                    <Col lg="12" className="my-3">
                      <label className="fw-semibold"> {t("roomType.columns.pricing")}</label>

                      {fields.map((field, index) => (
                        <PricingRow
                        ptypes={ptypes}
                        pctypes={pctypes}
                          key={field.id}
                          index={index}
                          register={register}
                          control={control}
                          setValue={setValue}
                          remove={remove}
                          canRemove={fields.length > 1}
                          pricingData={pricingData}
                          errors={errors}
                        />
                      ))}

                      <Button
                        type="button"
                        onClick={() =>
                          append({
                            pricingType: "hour",
                            priceCategory:"",
                            durationHours: "",
                            startDate:"",
                            endDate:"",
                            minStay: 1,
                            basePrice: "",
                            discount: 0,
                            finalPrice: 0,
                          })
                        }
                        className="commonBtn GreyBtn"
                      >
                        + {t("roomType.columns.addPricing")}
                      </Button>

                      {errors?.pricingData && (
                        <p className="text-danger mt-2">
                          {errors.pricingData.message}
                        </p>
                      )}
                    </Col>



                    <Col lg="12" className="my-2">
                      <label className="fw-semibold">{t("roomType.columns.roomAmenities")}</label>
                      <div
                        className="d-flex flex-wrap gap-3 p-2 border rounded"
                        style={{ maxHeight: "200px", overflowY: "auto" }}
                      >
                        {roomAmenities?.length > 0
                          ? roomAmenities.map((amenity) => (
                            <div
                              key={amenity._id}
                              className="checkboxlebels d-flex align-items-center"
                              style={{ minWidth: "150px" }}
                            >
                              <FormGroup check>
                                <Label check>
                                  <Input
                                    type="checkbox"
                                    className="chekboxCustom"
                                    label={amenity.name}
                                    checked={watch("amenities")?.includes(
                                      amenity._id
                                    )}
                                    onChange={(e) =>
                                      handleCheckbox(
                                        "amenities",
                                        amenity._id,
                                        e.target.checked
                                      )
                                    }
                                  />{" "}
                                  {amenity.name}
                                </Label>
                              </FormGroup>
                            </div>
                          ))
                          : Array.from({ length: 6 }).map((_, idx) => (
                            <Placeholder key={idx} xs={8} bg="secondary" />
                          ))}
                      </div>
                      {errors.amenities && (
                        <p className="text-danger">
                          {errors.amenities.message}
                        </p>
                      )}
                    </Col>
                    <Col lg="12" className="my-2">
                      <label>{t("roomType.columns.description")}</label>
                      <textarea
                        rows={3}
                        className="form-control"
                        {...register("description")}
                      />
                    </Col>

                    <Col lg="12" className="my-2">
                      <div
                        className="formWrpper rounded p-3"
                        style={{ background: "#f9f9f9" }}
                      >
                        <label className="fw-bold text-muted ps-2 m-0">
                          {t("roomType.columns.uploadImages")}
                        </label>
                        <div
                          className="border border-2 border-dashed rounded d-flex flex-column align-items-center justify-content-center p-4 bg-light"
                          style={{ cursor: "pointer", minHeight: "140px" }}
                          onClick={() =>
                            document.getElementById("imageUploadInput").click()
                          }
                        >
                          <i className="bi bi-cloud-upload fs-1 text-muted"></i>
                          <p className="m-0 text-muted">
                            {t("roomType.columns.imageLabel")}
                          </p>
                          <input
                            id="imageUploadInput"
                            type="file"
                            accept="image/*"
                            multiple
                            className="d-none"
                            onChange={handleImageChange}
                          />
                        </div>

                        {errors.images && (
                          <p className="text-danger mt-1">
                            {errors.images.message}
                          </p>
                        )}

                        <div className="d-flex flex-wrap gap-2 mt-3">
                          {imgloader ? (
                            <div
                              style={{ height: 120, Width: 120 }}
                              className="d-flex align-items-center justify-content-center border rounded"
                            >
                              Loading...
                            </div>
                          ) : (
                            imagePreviews.map((img, index) => (
                              <div
                                key={img._id}
                                style={{ Width: 120, height: 120, position: "relative" }}
                                className="border rounded overflow-hidden shadow-sm"
                              >
                                <img
                                  src={img.link}
                                  alt={`preview-${index}`}
                                  className="w-100 h-100 object-fit-cover"
                                />

                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 rounded-circle"
                                  onClick={() => handleRemoveImage(index)}
                                >
                                  ✕
                                </button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </Col>

                    <Col lg="12" className="my-2">
                      <div className="d-flex align-items-center justify-content-center gap-10">
                        <Button
                          type="button"
                          onClick={handleCancel}
                          className="commonBtn GreyBtn"
                        >
                          {t("common.cancel")}
                        </Button>
                        <Button type="submit" className="commonBtn">
                          {loader ? "Submitting..." : t("common.submit")}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddRoom;
